/**
 * Role Menu Helper
 * 
 * Helper functions สำหรับจัดการ menu structure
 * - ดึง menu structure จาก sidebar
 * - แสดงเมนูแบบ hierarchical (parent + children)
 */

import { MENU_PERMISSIONS } from '@/hooks/use-permission'

// ============================================
// Types
// ============================================

export interface MenuItem {
  path: string
  label: string
  labelTh: string
  children?: MenuItem[]
}

export interface MenuPermission {
  path: string
  enabled: boolean
  actions: {
    view: boolean
    create: boolean
    edit: boolean
    delete: boolean
    export: boolean
    approve: boolean
  }
}

// ============================================
// Menu Labels (จาก i18n)
// ============================================

const MENU_LABELS: Record<string, string> = {
  '/dashboard': 'แดชบอร์ด',
  '/members': 'สมาชิก',
  '/transactions': 'ธุรกรรม',
  '/transactions/smart-cashier': 'คอนโซลฝากถอนอัจฉริยะ',
  '/transactions/slips': 'รายการสลิป',
  '/banks': 'บัญชีธนาคาร',
  '/promotions': 'โปรโมชั่น',
  '/lossback': 'คืนยอดเสีย',
  '/lossback/manage': 'จัดการคืนยอดเสีย',
  '/lossback/records': 'รายการยอดเสีย',
  '/member-groups': 'กลุ่มสมาชิก',
  '/games': 'จัดการเกม',
  '/games/list': 'รายการเกม',
  '/games/providers': 'ผู้ให้บริการ',
  '/games/categories': 'หมวดหมู่',
  '/games/settings': 'ตั้งค่าเกม',
  '/games/report': 'รายงานเกม',
  '/marketing': 'เครื่องมือการตลาด',
  '/marketing/banners': 'แบนเนอร์',
  '/marketing/campaigns': 'แคมเปญ',
  '/marketing/popups': 'ป๊อปอัพ',
  '/marketing/sms': 'SMS Marketing',
  '/marketing/line': 'LINE Marketing',
  '/marketing/email': 'Email Marketing',
  '/marketing/push': 'Push Notification',
  '/marketing/landing': 'Landing Page',
  '/affiliates': 'พันธมิตร',
  '/affiliates/list': 'รายชื่อพันธมิตร',
  '/affiliates/commissions': 'ค่าคอมมิชชั่น',
  '/affiliates/links': 'ลิงก์แนะนำ',
  '/affiliates/payouts': 'การจ่ายเงิน',
  '/affiliates/report': 'รายงานพันธมิตร',
  '/reports': 'รายงาน',
  '/reports/monthly': 'การเงินรายเดือน',
  '/reports/financial': 'การเงิน',
  '/reports/members': 'สมาชิก',
  '/reports/transactions': 'ธุรกรรม',
  '/reports/banks': 'ธนาคาร',
  '/cron-jobs': 'Cron Jobs',
  '/settings': 'ตั้งค่า',
  '/admins': 'ผู้ดูแลระบบ',
  '/acl': 'จัดการสิทธิ์',
  '/audit': 'Audit Log',
  '/devices': 'จัดการอุปกรณ์',
  '/changelog': 'ประวัติการเปลี่ยนแปลง',
}

// ============================================
// Menu Structure (จาก sidebar - ครบทุกเมนู)
// ============================================

const MENU_STRUCTURE: MenuItem[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    labelTh: MENU_LABELS['/dashboard'] || 'แดชบอร์ด',
  },
  {
    path: '/members',
    label: 'Members',
    labelTh: MENU_LABELS['/members'] || 'สมาชิก',
  },
  {
    path: '/transactions',
    label: 'Transactions',
    labelTh: MENU_LABELS['/transactions'] || 'ธุรกรรม',
    children: [
      { 
        path: '/transactions/smart-cashier', 
        label: 'Smart Cashier', 
        labelTh: MENU_LABELS['/transactions/smart-cashier'] || 'Smart Cashier' 
      },
      { 
        path: '/transactions/slips', 
        label: 'Slips', 
        labelTh: MENU_LABELS['/transactions/slips'] || 'สลิป' 
      },
    ],
  },
  {
    path: '/banks',
    label: 'Banks',
    labelTh: MENU_LABELS['/banks'] || 'ธนาคาร',
  },
  {
    path: '/promotions',
    label: 'Promotions',
    labelTh: MENU_LABELS['/promotions'] || 'โปรโมชั่น',
  },
  {
    path: '/lossback',
    label: 'Lossback',
    labelTh: MENU_LABELS['/lossback'] || 'คืนยอดเสีย',
    children: [
      { 
        path: '/lossback/manage', 
        label: 'Manage Lossback', 
        labelTh: MENU_LABELS['/lossback/manage'] || 'จัดการคืนยอดเสีย' 
      },
      { 
        path: '/lossback/records', 
        label: 'Loss Records', 
        labelTh: MENU_LABELS['/lossback/records'] || 'รายการยอดเสีย' 
      },
    ],
  },
  {
    path: '/member-groups',
    label: 'Member Groups',
    labelTh: MENU_LABELS['/member-groups'] || 'กลุ่มสมาชิก',
  },
  {
    path: '/games',
    label: 'Games',
    labelTh: MENU_LABELS['/games'] || 'เกม',
    children: [
      { 
        path: '/games/list', 
        label: 'Game List', 
        labelTh: MENU_LABELS['/games/list'] || 'รายการเกม' 
      },
      { 
        path: '/games/providers', 
        label: 'Providers', 
        labelTh: MENU_LABELS['/games/providers'] || 'ผู้ให้บริการ' 
      },
      { 
        path: '/games/categories', 
        label: 'Categories', 
        labelTh: MENU_LABELS['/games/categories'] || 'หมวดหมู่' 
      },
      { 
        path: '/games/settings', 
        label: 'Settings', 
        labelTh: MENU_LABELS['/games/settings'] || 'ตั้งค่าเกม' 
      },
      { 
        path: '/games/report', 
        label: 'Report', 
        labelTh: MENU_LABELS['/games/report'] || 'รายงานเกม' 
      },
    ],
  },
  {
    path: '/marketing',
    label: 'Marketing',
    labelTh: MENU_LABELS['/marketing'] || 'การตลาด',
    children: [
      { 
        path: '/marketing/banners', 
        label: 'Banners', 
        labelTh: MENU_LABELS['/marketing/banners'] || 'แบนเนอร์' 
      },
      { 
        path: '/marketing/campaigns', 
        label: 'Campaigns', 
        labelTh: MENU_LABELS['/marketing/campaigns'] || 'แคมเปญ' 
      },
      { 
        path: '/marketing/popups', 
        label: 'Popups', 
        labelTh: MENU_LABELS['/marketing/popups'] || 'ป๊อปอัพ' 
      },
      { 
        path: '/marketing/sms', 
        label: 'SMS', 
        labelTh: MENU_LABELS['/marketing/sms'] || 'SMS' 
      },
      { 
        path: '/marketing/line', 
        label: 'LINE', 
        labelTh: MENU_LABELS['/marketing/line'] || 'LINE' 
      },
      { 
        path: '/marketing/email', 
        label: 'Email', 
        labelTh: MENU_LABELS['/marketing/email'] || 'Email' 
      },
      { 
        path: '/marketing/push', 
        label: 'Push', 
        labelTh: MENU_LABELS['/marketing/push'] || 'Push' 
      },
      { 
        path: '/marketing/landing', 
        label: 'Landing Page', 
        labelTh: MENU_LABELS['/marketing/landing'] || 'Landing Page' 
      },
    ],
  },
  {
    path: '/reports',
    label: 'Reports',
    labelTh: MENU_LABELS['/reports'] || 'รายงาน',
    children: [
      { 
        path: '/reports/monthly', 
        label: 'Monthly', 
        labelTh: MENU_LABELS['/reports/monthly'] || 'รายงานรายเดือน' 
      },
      { 
        path: '/reports/financial', 
        label: 'Financial', 
        labelTh: MENU_LABELS['/reports/financial'] || 'รายงานการเงิน' 
      },
      { 
        path: '/reports/members', 
        label: 'Members', 
        labelTh: MENU_LABELS['/reports/members'] || 'รายงานสมาชิก' 
      },
      { 
        path: '/reports/transactions', 
        label: 'Transactions', 
        labelTh: MENU_LABELS['/reports/transactions'] || 'รายงานธุรกรรม' 
      },
      { 
        path: '/reports/banks', 
        label: 'Banks', 
        labelTh: MENU_LABELS['/reports/banks'] || 'รายงานธนาคาร' 
      },
    ],
  },
  {
    path: '/affiliates',
    label: 'Affiliates',
    labelTh: MENU_LABELS['/affiliates'] || 'พันธมิตร',
    children: [
      { 
        path: '/affiliates/list', 
        label: 'List', 
        labelTh: MENU_LABELS['/affiliates/list'] || 'รายชื่อพันธมิตร' 
      },
      { 
        path: '/affiliates/commissions', 
        label: 'Commissions', 
        labelTh: MENU_LABELS['/affiliates/commissions'] || 'ค่าคอมมิชชั่น' 
      },
      { 
        path: '/affiliates/links', 
        label: 'Links', 
        labelTh: MENU_LABELS['/affiliates/links'] || 'ลิงก์แนะนำ' 
      },
      { 
        path: '/affiliates/payouts', 
        label: 'Payouts', 
        labelTh: MENU_LABELS['/affiliates/payouts'] || 'การจ่ายเงิน' 
      },
      { 
        path: '/affiliates/report', 
        label: 'Report', 
        labelTh: MENU_LABELS['/affiliates/report'] || 'รายงานพันธมิตร' 
      },
    ],
  },
  {
    path: '/admins',
    label: 'Admins',
    labelTh: MENU_LABELS['/admins'] || 'ผู้ดูแล',
  },
  {
    path: '/acl',
    label: 'ACL',
    labelTh: MENU_LABELS['/acl'] || 'จัดการสิทธิ์',
  },
  {
    path: '/cron-jobs',
    label: 'Cron Jobs',
    labelTh: MENU_LABELS['/cron-jobs'] || 'Cron Jobs',
  },
  {
    path: '/audit',
    label: 'Audit Log',
    labelTh: MENU_LABELS['/audit'] || 'Audit Log',
  },
  {
    path: '/devices',
    label: 'Devices',
    labelTh: MENU_LABELS['/devices'] || 'อุปกรณ์',
  },
  {
    path: '/changelog',
    label: 'Changelog',
    labelTh: MENU_LABELS['/changelog'] || 'Changelog',
  },
  {
    path: '/settings',
    label: 'Settings',
    labelTh: MENU_LABELS['/settings'] || 'ตั้งค่า',
  },
]

// ============================================
// Helper Functions
// ============================================

export const RoleMenuHelper = {
  /**
   * ดึง menu structure ทั้งหมด
   */
  getMenuStructure(): MenuItem[] {
    return MENU_STRUCTURE
  },

  /**
   * ดึง menu item ตาม path
   */
  getMenuItem(path: string): MenuItem | null {
    const findInStructure = (items: MenuItem[]): MenuItem | null => {
      for (const item of items) {
        if (item.path === path) {
          return item
        }
        if (item.children) {
          const found = findInStructure(item.children)
          if (found) return found
        }
      }
      return null
    }
    
    return findInStructure(MENU_STRUCTURE)
  },

  /**
   * ดึง menu paths ทั้งหมด (รวม children)
   */
  getAllMenuPaths(): string[] {
    const paths: string[] = []
    
    const traverse = (items: MenuItem[]) => {
      for (const item of items) {
        paths.push(item.path)
        if (item.children) {
          traverse(item.children)
        }
      }
    }
    
    traverse(MENU_STRUCTURE)
    return paths
  },

  /**
   * ตรวจสอบว่า path อยู่ใน MENU_PERMISSIONS หรือไม่
   */
  isPathInPermissions(path: string): boolean {
    return path in MENU_PERMISSIONS
  },
}
